import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { ArrowRight, Menu, X } from 'lucide-react';
import Footer from '@/components/shared/Footer';

export default function LandingPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const nav = useNavigate();
  const de = language === 'de';
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-bone" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-bone/95 backdrop-blur-sm border-b border-border/20" data-testid="landing-nav">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <span className="font-display text-2xl md:text-3xl font-semibold tracking-tight cursor-pointer" onClick={() => nav('/')}>
            <span className="text-terracotta">bel</span>osophie
          </span>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => nav('/begleitung')} className="font-body text-sm font-medium text-foreground/60 hover:text-terracotta transition-colors">Meine Begleitung</button>
            <button onClick={() => nav('/about')} className="font-body text-sm font-medium text-foreground/60 hover:text-terracotta transition-colors">Über mich</button>
            <button onClick={() => nav('/stil')} className="font-body text-sm font-medium text-foreground/60 hover:text-terracotta transition-colors">Stil & Vitalität</button>
            <button onClick={() => nav('/coaching-in-motion')} className="font-body text-sm font-medium text-foreground/60 hover:text-terracotta transition-colors">Coaching in Motion</button>
            <button onClick={() => nav('/blog')} className="font-body text-sm font-medium text-foreground/60 hover:text-terracotta transition-colors">Blog</button>
            {user ? (
              <Button onClick={() => nav('/dashboard')} size="sm" className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-5">Dashboard</Button>
            ) : (
              <Button onClick={() => nav('/auth')} size="sm" className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-5">Anmelden</Button>
            )}
          </div>
          <div className="flex md:hidden items-center gap-3">
            <Button onClick={() => nav(user ? '/dashboard' : '/auth')} size="sm" className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-4 text-xs">
              {user ? 'Dashboard' : 'Anmelden'}
            </Button>
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-1 text-foreground/60"><Menu size={24} /></button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-border/20 bg-bone px-6 py-4 space-y-3">
            <button onClick={() => { nav('/begleitung'); setMenuOpen(false); }} className="block font-body text-sm font-medium text-foreground/70 py-1 w-full text-left">Meine Begleitung</button>
            <button onClick={() => { nav('/about'); setMenuOpen(false); }} className="block font-body text-sm font-medium text-foreground/70 py-1 w-full text-left">Über mich</button>
            <button onClick={() => { nav('/stil'); setMenuOpen(false); }} className="block font-body text-sm font-medium text-foreground/70 py-1 w-full text-left">Stil & Vitalität</button>
            <button onClick={() => { nav('/coaching-in-motion'); setMenuOpen(false); }} className="block font-body text-sm font-medium text-foreground/70 py-1 w-full text-left">Coaching in Motion</button>
            <button onClick={() => { nav('/blog'); setMenuOpen(false); }} className="block font-body text-sm font-medium text-foreground/70 py-1 w-full text-left">Blog</button>
            <button onClick={() => { nav('/pricing'); setMenuOpen(false); }} className="block font-body text-sm font-medium text-foreground/70 py-1 w-full text-left">Preise</button>
          </div>
        )}
      </nav>

      {/* Hero Image */}
      <section className="max-w-5xl mx-auto px-6 pb-4">
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img src="https://images.unsplash.com/photo-1461468611824-46457c0e11fd?w=1200&q=80" alt="Frau im warmen Sonnenlicht – Moment der Selbstreflexion und inneren Ruhe" className="w-full h-[280px] md:h-[400px] object-cover object-center" />
        </div>
      </section>

      {/* Hero – "Sie spricht von mir" */}
      <section className="max-w-4xl mx-auto px-6 pt-16 md:pt-24 pb-12">
        <div className="space-y-6 animate-fade-in">
          <p className="font-body text-foreground/50 leading-relaxed text-base md:text-lg max-w-2xl">
            Du funktionierst. Du kümmerst dich. Du trägst Verantwortung.<br />
            Du bist die Frau, auf die andere zählen.
          </p>
          <div className="space-y-4">
            <p className="font-body text-foreground/40 text-sm leading-relaxed max-w-xl">
              Du organisierst. Du entscheidest. Du hältst vieles zusammen.
              Vielleicht hast du beruflich viel erreicht. Vielleicht hast du eine Familie begleitet.
              Vielleicht hast du Herausforderungen gemeistert, von denen andere gar nichts wissen.
            </p>
          </div>
          <div className="pt-4">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] text-foreground">
              Und trotzdem gibt es manchmal<br />diesen stillen Moment:<br />
              <em className="italic text-terracotta">Wo bin eigentlich ich geblieben?</em>
            </h1>
          </div>
          <p className="font-body text-foreground/50 text-base leading-relaxed max-w-xl pt-2">
            Nicht, weil dein Leben falsch ist. Nicht, weil du unzufrieden sein musst.
            Sondern weil du spürst: Da ist noch eine Frau in dir, die mehr Raum bekommen möchte.
            Eine Frau mit eigenen Wünschen. Mit eigener Lebendigkeit. Mit Neugier auf das, was noch kommt.
          </p>
        </div>
      </section>

      {/* "Vielleicht erkennst du dich wieder" */}
      <section className="bg-warmgray/50">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <p className="font-display text-2xl md:text-3xl font-medium text-foreground/80 mb-10">Vielleicht erkennst du dich wieder ...</p>
          <div className="space-y-6">
            {[
              'Du bist die Frau, die immer für andere da war und sich selbst dabei etwas aus den Augen verloren hat.',
              'Oder du bist die Frau, die alles im Griff hat und gelernt hat, stark zu sein – aber manchmal spürt, wie anstrengend es ist, immer stark zu bleiben.',
              'Oder du bist die Frau, die das Leben liebt, viel erlebt und viel unternimmt – und sich trotzdem manchmal fragt, was sie eigentlich wirklich erfüllt.',
            ].map((text, i) => (
              <p key={i} className="font-body text-foreground/60 text-base leading-relaxed pl-4 border-l-2 border-terracotta/30">
                {text}
              </p>
            ))}
          </div>
          <p className="font-body text-foreground/50 text-base leading-relaxed mt-10 max-w-xl">
            Ganz egal, welcher Seite du dich näher fühlst:<br />
            <span className="text-foreground/70 font-medium">Vielleicht ist jetzt die Zeit, wieder bewusster bei dir anzukommen.</span>
          </p>
        </div>
      </section>

      {/* "Was ich anbiete" */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-8">Was ich anbiete</h2>
        <div className="space-y-3 mb-10">
          {[
            'Keine klassische Beratung.',
            'Kein Programm nach Schema F.',
            'Keine vorgefertigten Antworten.',
          ].map((text, i) => (
            <p key={i} className="font-body text-foreground/40 text-sm">{text}</p>
          ))}
        </div>
        <p className="font-body text-foreground/60 text-base leading-relaxed max-w-xl mb-6">
          Ich begleite Frauen in der Lebensmitte in einer persönlichen, individuellen Begegnung.
          Ich höre zu. Ich stelle Fragen. Ich erkenne Zusammenhänge. Ich spiegle, was vielleicht selbst noch nicht sichtbar ist.
        </p>
        <div className="bg-gradient-to-r from-terracotta/5 to-amber-50/50 border-l-4 border-terracotta rounded-r-2xl p-8 my-10">
          <p className="font-display text-2xl md:text-3xl font-medium text-foreground/80 italic leading-relaxed">
            Ich schenke Frauen nicht meine Antworten.<br />
            Ich schenke ihnen meinen Blick.
          </p>
          <p className="font-body text-foreground/40 text-sm mt-3">Einen Blick, der hilft, sich selbst wieder klarer zu sehen.</p>
        </div>
      </section>

      {/* "Was ich nicht anbiete" + "Was du findest" */}
      <section className="bg-warmgray/50">
        <div className="max-w-4xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12">
          <div>
            <h3 className="font-display text-xl font-medium mb-6 text-foreground/70">Was ich nicht verspreche</h3>
            <div className="space-y-3">
              {[
                'Ein neues Leben über Nacht.',
                'Eine perfekte Version von dir.',
                'Eine Anleitung, wie du sein solltest.',
              ].map((text, i) => (
                <p key={i} className="font-body text-foreground/40 text-sm pl-4 border-l border-foreground/10">{text}</p>
              ))}
            </div>
            <p className="font-body text-foreground/50 text-sm mt-6 leading-relaxed">
              Denn du bist kein Projekt.<br />
              Du bist eine Frau mit Geschichte. Mit Erfahrungen. Mit Stärken. Mit Wünschen.
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-medium mb-6 text-foreground/70">Was du bei mir findest</h3>
            <div className="space-y-4">
              {[
                'Eine ehrliche Begegnung.',
                'Klarheit.',
                'Neue Perspektiven.',
                'Eine Begleitung, die sich deinem Leben anpasst.',
              ].map((text, i) => (
                <p key={i} className="font-body text-foreground/60 text-base font-medium">{text}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <Button onClick={() => nav('/begleitung')} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-10 py-6 text-lg font-medium shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-all" data-testid="hero-cta-btn">
          Lernen wir uns kennen <ArrowRight size={20} className="ml-2" />
        </Button>
        <p className="font-body text-xs text-foreground/30 mt-4">info@belosophie.com</p>
      </section>

      <Footer />
    </div>
  );
}
